'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import { useRef } from 'react';

import { usePageContext } from '@commons/contexts/page';
import { useMediaQueries } from '@commons/contexts/responsive';

// GSAP Plugins registration.

gsap.registerPlugin(useGSAP, TextPlugin);

// Type definition for the Typewritter component properties.

type Props = {
  text: string;
};

/**
 * Typewritter component.
 *
 * Uses GSAP TextPlugin to do a letter by letter animation of the text.
 *
 * @see https://gsap.com/resources/React/
 * @see https://gsap.com/docs/v3/Plugins/TextPlugin/
 */

const Typewritter = ({ text }: Props) => {
  const ref = useRef<HTMLSpanElement>(null);
  const { isEditing } = usePageContext();
  const { ready, minWidth } = useMediaQueries();

  // Disable the effect if on mobile or in the page builder.
  // Also disable during SSR or until the component is mounted.
  const disabled = !ready || !minWidth.md || isEditing;

  useGSAP(
    () => {
      if (!ref.current || disabled) return;

      gsap.to('.text', {
        text: {
          speed: 1,
          value: text,
          preserveSpaces: true,
        },
        delay: 0.2,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: 'bottom 50%',
        },
      });
    },
    { scope: ref, dependencies: [disabled] }
  );

  if (disabled) {
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  }

  return (
    <span ref={ref}>
      <span className="text" />
    </span>
  );
};

export default Typewritter;
